// src/pages/AttendancePage/components/MobileAttendanceList.jsx
import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
	faExclamationTriangle,
	faCheck,
	faTimes,
	faUsers,
} from "@fortawesome/free-solid-svg-icons";
import styles from "./MobileAttendanceList.module.css";

const MobileAttendanceList = ({
	students,
	currentDate,
	formatDate,
	getCurrentPeriods,
	getAttendanceStatus,
	openStatusModal,
}) => {
	// Get overall day status for a student
	const getStudentDayStatus = (studentId) => {
		const periods = getCurrentPeriods();
		const statuses = periods.map((period) =>
			getAttendanceStatus(studentId, period)
		);

		// Count different status types
		const hasUnexcused = statuses.includes("unexcused");
		const hasPending = statuses.includes("pending");
		const hasExcused = statuses.includes("excused");
		const hasNoClass = statuses.includes("no-class");
		const hasPresent = statuses.includes("present");

		// Logic for overall day status
		if (hasUnexcused) {
			return "unexcused"; // Any unexcused absence = red
		} else if (hasPending) {
			return "pending"; // Any pending absence = yellow
		} else if (hasExcused && !hasPresent) {
			return "excused"; // Only excused absences = green
		} else if (hasPresent || hasExcused) {
			return "present"; // Has any present periods = blue
		} else if (hasNoClass) {
			return "no-class"; // All periods had no class = no indicator
		} else {
			return "present"; // Default to present
		}
	};

	// Get absences for a student for the current day (for modal opening)
	const getStudentAbsences = (studentId) => {
		const periods = getCurrentPeriods();
		const absences = [];

		periods.forEach((period) => {
			const status = getAttendanceStatus(studentId, period);
			if (
				status === "pending" ||
				status === "excused" ||
				status === "unexcused"
			) {
				absences.push({
					period,
					status,
				});
			}
		});

		return absences;
	};

	// Render day status indicator
	const renderDayStatusIndicator = (studentId) => {
		const dayStatus = getStudentDayStatus(studentId);
		const absences = getStudentAbsences(studentId);

		if (dayStatus === "no-class") {
			return null; // No indicator for no class days
		}

		let icon, iconColor, bgColor, label;

		switch (dayStatus) {
			case "present":
				icon = faCheck;
				iconColor = "#1976d2";
				bgColor = "#e3f2fd";
				label = "Present";
				break;
			case "excused":
				icon = faCheck;
				iconColor = "#388e3c";
				bgColor = "#e8f5e8";
				label = "Excused";
				break;
			case "unexcused":
				icon = faTimes;
				iconColor = "#d32f2f";
				bgColor = "#ffebee";
				label = "Unexcused";
				break;
			case "pending":
				icon = faExclamationTriangle;
				iconColor = "#f57c00";
				bgColor = "#fff8e1";
				label = "Pending";
				break;
			default:
				return null;
		}

		// If there are absences, make it clickable
		const hasAbsences = absences.length > 0;

		return (
			<div
				className={`${styles.dayStatusIndicator} ${
					hasAbsences ? styles.clickable : ""
				}`}
				style={{
					color: iconColor,
					backgroundColor: bgColor,
					border: `2px solid ${iconColor}`,
				}}
				onClick={
					hasAbsences
						? () => {
								// Open modal for the first absence (you can modify this logic)
								const firstAbsence = absences[0];
								openStatusModal(studentId, firstAbsence.period);
						  }
						: undefined
				}
				title={hasAbsences ? `${label} - Click to manage absences` : label}
			>
				<FontAwesomeIcon icon={icon} />
				{hasAbsences && absences.length > 1 && (
					<span className={styles.absenceCount}>{absences.length}</span>
				)}
			</div>
		);
	};

	return (
		<div className={styles.mobileAttendanceList}>
			<div className={styles.listHeader}>
				<FontAwesomeIcon icon={faUsers} />
				<span>Students ({students.length})</span>
			</div>

			<div className={styles.studentsList}>
				{students.map((student) => {
					return (
						<div key={student.id} className={styles.studentItem}>
							<div className={styles.studentInfo}>
								<span className={styles.studentName}>{student.name}</span>
								<span className={styles.studentId}>{student.studentId}</span>
							</div>

							{renderDayStatusIndicator(student.id)}
						</div>
					);
				})}

				{students.length === 0 && (
					<div className={styles.emptyState}>
						<FontAwesomeIcon icon={faUsers} />
						<p>No students found.</p>
					</div>
				)}
			</div>
		</div>
	);
};

export default MobileAttendanceList;
